// Shared WorkbenchAttachSelect — tiny inline attach widget used by the
// Airframe and Environment centre-pane sections when nothing is yet
// attached. Fires the attach mutation via the supplied `onAttach`
// handler, then calls `onScenarioChanged()` so the scenario tree,
// evidence rail, and the pane itself all refetch. A detach action is
// exposed via the header once an attachment exists.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import SpinnerButton from "@/components/SpinnerButton";
import EmptyState from "@/components/EmptyState";

const NONE = "__none__";

export default function WorkbenchAttachSelect({
    sectionLabel,
    listFn,
    onAttach,
    libraryPath,
    emptyTitle,
    emptyBody,
    placeholder = "Pick one…",
    testIdPrefix,
    readOnly = false,
    onAttached,
}) {
    const [list, setList] = useState([]);
    const [sel, setSel] = useState(NONE);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const items = await listFn();
                if (alive) setList(Array.isArray(items) ? items : []);
            } catch (e) {
                toast.error(formatApiError(e, "Load failed"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, [listFn]);

    const commit = async () => {
        if (sel === NONE) return;
        setBusy(true);
        try {
            await onAttach(sel);
            toast.success(`${sectionLabel} attached`);
            onAttached?.();
            setSel(NONE);
        } catch (e) {
            toast.error(formatApiError(e, "Attach failed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid={`${testIdPrefix}-empty`}>
            <EmptyState title={emptyTitle} body={emptyBody} />
            {loading ? (
                <div className="text-xs text-ux-text-3 font-mono">Loading library…</div>
            ) : list.length === 0 ? (
                <div className="text-xs text-ux-text-3 font-mono">
                    No items in the library yet.{" "}
                    <Link to={libraryPath} className="text-ux-accent underline">
                        Open the library
                    </Link>{" "}
                    to create one.
                </div>
            ) : (
                <div
                    className="flex flex-wrap items-end gap-3 border border-ux-border-1 rounded-md bg-ux-bg-1 p-3"
                    data-testid={`${testIdPrefix}-attach-row`}
                >
                    <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                        {sectionLabel}
                        <Select value={sel} onValueChange={setSel} disabled={busy || readOnly}>
                            <SelectTrigger
                                className="w-[300px] bg-ux-bg-2 text-[12px] font-mono"
                                data-testid={`${testIdPrefix}-select`}
                            >
                                <SelectValue placeholder={placeholder} />
                            </SelectTrigger>
                            <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                                <SelectItem value={NONE} className="text-[12px] font-mono">
                                    {placeholder}
                                </SelectItem>
                                {list.map((it) => (
                                    <SelectItem
                                        key={it.id}
                                        value={it.id}
                                        className="text-[12px] font-mono"
                                        data-testid={`${testIdPrefix}-option-${it.id}`}
                                    >
                                        {it.name}
                                        {it.is_preset ? " · preset" : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </label>
                    <SpinnerButton
                        size="sm"
                        disabled={sel === NONE || readOnly}
                        loading={busy}
                        onClick={commit}
                        testId={`${testIdPrefix}-confirm`}
                    >
                        Attach
                    </SpinnerButton>
                    <Link
                        to={libraryPath}
                        className="ml-auto text-xs text-ux-accent underline decoration-dotted"
                        data-testid={`${testIdPrefix}-open-library`}
                    >
                        Open library <ExternalLink className="inline w-3 h-3" />
                    </Link>
                </div>
            )}
        </div>
    );
}
